import type {ImageField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import type {HeroV2SliceFullWidthImagePrimary} from 'prismicio-types'
import type {IconType} from 'src/common/Icon'
import {Icon} from 'src/common/Icon'
import {getIconName} from 'src/common/helpers'

type HeroV2FullWidthImageProps = {
  slice: {
    primary: HeroV2SliceFullWidthImagePrimary
    variation: string
    slice_type: string
  }
  pageUid: string
  componentIndex: number
  companyLogoOverride?: ImageField
  mainImageOverride?: ImageField
}

export const HeroV2FullWidthImage = ({
  slice: {
    primary: {background_image, heading, hightlights},
  },
  companyLogoOverride,
  mainImageOverride,
}: HeroV2FullWidthImageProps) => (
  <div className="relative max-md:flex max-md:flex-col">
    <div
      className="bg-cover bg-[right_center] max-md:order-2 max-md:aspect-square md:aspect-auto md:h-[375px] md:w-full md:max-lg:max-h-[375px] lg:h-[720px] xl:aspect-video"
      style={{
        backgroundImage: `url(${isFilled.image(background_image) ? background_image.url : (mainImageOverride?.url ?? '')})`,
      }}
    />
    <div className="lg:absolute lg:bottom-[96px] lg:left-10 lg:top-[137px] xl:left-1/2 xl:w-full xl:max-w-[1200px] xl:-translate-x-1/2">
      <div className="flex flex-col gap-10 bg-green-dark px-5 pb-10 pt-[96px] md:flex-row md:gap-16 md:px-10 lg:h-auto lg:w-1/2 lg:flex-col lg:justify-between lg:rounded-lg lg:p-16">
        <div className="flex flex-col gap-8 md:w-full">
          {isFilled.image(companyLogoOverride) && (
            <PrismicNextImage field={companyLogoOverride} className="h-8 w-fit opacity-60" />
          )}
          <h1
            className="text-tg-h2 xl:text-h1 font-medium leading-[normal] text-moss-white"
            style={{textWrap: 'balance'}}
          >
            {heading}
          </h1>
        </div>
        {hightlights.length > 0 && (
          <div className="flex flex-col gap-6 md:w-full md:max-w-[216px] md:gap-8 lg:min-w-full lg:flex-row lg:gap-12">
            {hightlights.map((highlight, index) => {
              const iconName = getIconName(highlight.icon) as IconType

              return (
                <div key={highlight.text! + index} className="flex flex-col lg:w-full lg:max-w-[200px]">
                  <div className="flex items-center gap-1">
                    <Icon name={iconName} className="size-3 text-neon-450" />
                    <p className="text-body-bold-l font-medium text-moss-white">{highlight.text}</p>
                  </div>
                  <span className="text-h5 font-medium leading-6 text-moss-white/[.7] lg:text-wrap">
                    {highlight.subtext}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  </div>
)
